import { offset, shift } from "@floating-ui/dom";
import { autoUpdate, useFloating } from "@floating-ui/react";
import { faFilter } from "@fortawesome/pro-light-svg-icons";
import {
    faChevronsLeft,
    faChevronsRight,
} from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover, Portal } from "@headlessui/react";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import type { FeatureCollection } from "geojson";
import { useAtom } from "jotai";
import { type ReactNode, useMemo, useState } from "react";
import {
    type AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    EmissionRecordsListEventStatusEnum,
    EventStatusEnum,
    type InfrastructureSitesListEmissionStatsParameter,
} from "../../apiClient/generated";
import {
    useAccountsApiClient,
    useAnalyticsApiClient,
    useDataProvidersApi,
} from "../../hooks";
import { DateRangeField, NumberField } from "../../ui/Inputs";
import { readGeoFileAsGeoJson } from "../../utils/geopatialUtils";
import { GeoFilterArea } from "../filters/GeoFilterSelect";
import { DataProviderNestedFilter } from "../filters/ProviderFilter";
import { siteEmissionSortFieldAtom } from "../Infrastructure/Table/Sites.atoms";
import { MenuCategory } from "../MapV2/ui/Menu";
import { DECISION_FILTER_OPTIONS } from "../UnmatchedData/decisions";
import UploadDropdown from "../UploadDropdown";
import type {
    LocationFilterState,
    RangeFilterState,
    TableDateFilterState,
} from "./state";

interface BaseHeaderFilterProps {
    isFiltered: boolean;
    children: ReactNode;
}

const BaseHeaderFilter = (props: BaseHeaderFilterProps) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom",
        middleware: [
            offset({
                mainAxis: 20,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <div ref={refs.setReference}>
            <Popover>
                <Popover.Button className="flex items-center mr-2">
                    {props.isFiltered ? (
                        <FontAwesomeIcon icon={faFilterSolid} className="w-3" />
                    ) : (
                        <FontAwesomeIcon
                            icon={faFilter}
                            className="w-3 text-gray-400"
                        />
                    )}
                </Popover.Button>
                <Portal>
                    <Popover.Panel
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="min-w-56 max-h-96 overflow-auto rounded-md p-1 py-1 border-2 border-ae-slate-300 shadow-lg shadow-neutral-200 z-50 bg-white"
                    >
                        {props.children}
                        <hr className="my-1" />
                        <div className="flex">
                            <Popover.Button className="w-full flex justify-center font-bold">
                                Close
                            </Popover.Button>
                        </div>
                    </Popover.Panel>
                </Portal>
            </Popover>
        </div>
    );
};

interface BaseTextSearchFilterProps {
    isFiltered: boolean;
    filterValue?: string;
    setFilterValue: (newFilterValue?: string) => void;
}

export const BaseTextSearchFilter = (props: BaseTextSearchFilterProps) => (
    <BaseHeaderFilter isFiltered={props.isFiltered}>
        <div className="p-1 font-normal text-sm text-ae-blue-900">
            <p>Search for:</p>
            <input
                autoFocus={true}
                type="text"
                className="text-sm py-1 px-2 rounded-md mt-1 w-full"
                value={props.filterValue}
                onChange={(e) => props.setFilterValue(e.target.value)}
            />
        </div>
    </BaseHeaderFilter>
);

interface MultipleChoiceFilterProps {
    isFiltered: boolean;
    filterValue?: string[];
    setFilterValue: (newFilterValue?: string[]) => void;
}

interface MultipleChoiceFilterBaseProps {
    props: MultipleChoiceFilterProps;
    options: {
        id: string;
        displayValue: string;
    }[];
    searchable?: boolean;
}

const MultipleChoiceFilterBase = ({
    props,
    options,
    searchable,
}: MultipleChoiceFilterBaseProps) => {
    const [search, setSearch] = useState("");

    const setFilter = (value) => {
        const values = props.filterValue || [];
        if (values.includes(value)) {
            values.splice(values.indexOf(value), 1);
            props.setFilterValue(values.length > 0 ? values : undefined);
        } else {
            values.push(value);
            props.setFilterValue(values);
        }
    };

    const selecedItems = options.filter((i) =>
        props.filterValue?.includes(i.id),
    );
    const unselecedItems = options.filter((i) => {
        if (search.trim() !== "") {
            return (
                !props.filterValue?.includes(i.id) &&
                i.displayValue.toLowerCase().includes(search.toLowerCase())
            );
        }
        return !props.filterValue?.includes(i.id);
    });

    return (
        <BaseHeaderFilter isFiltered={props.isFiltered}>
            <div className="p-1">
                {searchable && (
                    <input
                        autoFocus={true}
                        type="text"
                        className="text-sm py-1 px-2 rounded-md mt-1 w-full mb-2"
                        placeholder="Type to search users"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                )}
                {selecedItems.map((option) => {
                    const active = (props.filterValue || []).includes(
                        option.id,
                    );
                    return (
                        <label
                            key={option.id}
                            className={`relative cursor-default select-none
                                flex gap-2 items-center px-2 py-1 rounded
                                ${
                                    active
                                        ? "bg-ae-slate-300"
                                        : "text-ae-slate-900"
                                }`}
                        >
                            <input
                                type="checkbox"
                                className="accent-ae-blue-500 focus:ring-0"
                                checked={active}
                                onChange={() => setFilter(option.id)}
                            />
                            <span
                                className={`block truncate capitalize ${
                                    active ? "font-bold" : "font-normal"
                                }`}
                            >
                                {option.displayValue}
                            </span>
                        </label>
                    );
                })}
                {unselecedItems.map((option, optionIdx) => {
                    const active = (props.filterValue || []).includes(
                        option.id,
                    );
                    return (
                        <label
                            key={option.id}
                            className={`relative cursor-default select-none
                                flex gap-2 items-center px-2 py-1 rounded
                                ${
                                    active
                                        ? "bg-ae-slate-300"
                                        : "text-ae-slate-900"
                                }`}
                        >
                            <input
                                type="checkbox"
                                className="accent-ae-blue-500 focus:ring-0"
                                checked={active}
                                onChange={() => setFilter(option.id)}
                            />
                            <span
                                className={`block truncate capitalize ${
                                    active ? "font-bold" : "font-normal"
                                }`}
                            >
                                {option.displayValue}
                            </span>
                        </label>
                    );
                })}
            </div>
        </BaseHeaderFilter>
    );
};

interface ParentFilterProps {
    isFiltered: boolean;
    filterValue?: string;
    setFilterValue: (newFilterValue?: string) => void;
}

export const ParentFilter = (props: ParentFilterProps) => (
    <BaseHeaderFilter isFiltered={props.isFiltered}>
        {props.isFiltered ? (
            <div className="p-1 font-normal text-sm text-ae-blue-900">
                <p>Filtering by parent id:</p>
                <p className="truncate">{props.filterValue}</p>
                <hr className="my-1" />
                <Popover.Button
                    className="w-full flex justify-center font-bold"
                    onClick={() => props.setFilterValue(undefined)}
                >
                    Clear filter
                </Popover.Button>
            </div>
        ) : (
            <p className="p-1 font-normal text-ae-blue-900">
                Use the filter icon on the table rows to filter by parent.
                <FunnelIcon className="mt-1 w-4" />
            </p>
        )}
    </BaseHeaderFilter>
);

interface LocationFilterProps {
    isFiltered: boolean;
    filterValue?: any;
    setFilterValue: (newFilterValue?: any) => void;
}

export const LocationFilter = (props: LocationFilterProps) => {
    const [error, setError] = useState(false);

    const setLocationFilter = (files: FileList) => {
        // Load shape from upload
        readGeoFileAsGeoJson({
            file: files[0],
            callback: (data, error) => {
                if (error) {
                    setError(true);
                    return;
                }
                if (
                    data.type === "FeatureCollection" &&
                    data.features.length > 0
                ) {
                    props.setFilterValue(
                        JSON.stringify(data.features[0].geometry),
                    );
                    setError(false);
                } else {
                    setError(false);
                }
            },
        });
    };

    return (
        <BaseHeaderFilter isFiltered={props.isFiltered}>
            {props.isFiltered ? (
                <div className="p-1 font-normal text-sm text-ae-blue-900">
                    <p>Filtering using GeoJSON/KML filter.</p>
                    <hr className="my-1" />
                    <Popover.Button
                        className="w-full flex justify-center font-bold"
                        onClick={() => props.setFilterValue(undefined)}
                    >
                        Clear filter
                    </Popover.Button>
                </div>
            ) : (
                <div className="p-1 font-normal text-ae-blue-900">
                    <p className="mb-1">
                        Drop or select a KML or GeoJSON file containing a
                        polygon to filter the table.
                    </p>
                    {error && (
                        <p className="font-bold text-red-500 mb-1">
                            The selected file could not be parsed.
                        </p>
                    )}
                    <UploadDropdown
                        accept=".geojson,.kml"
                        onChange={setLocationFilter}
                    />
                </div>
            )}
        </BaseHeaderFilter>
    );
};

interface LocationFilterV2Props {
    isFiltered: boolean;
    filterValue?: LocationFilterState;
    setFilterValue: (newFilterValue?: LocationFilterState) => void;
}

export const LocationFilterV2 = (props: LocationFilterV2Props) => {
    const [pendingShape, setPendingShape] = useState<FeatureCollection>();
    return (
        <BaseHeaderFilter isFiltered={props.isFiltered}>
            <div className="w-72">
                <GeoFilterArea
                    selectedPresets={props.filterValue?.publicPresets}
                    setSelectedPresets={(selection) => {
                        if (selection.length > 0) {
                            props.setFilterValue({
                                type: "location",
                                publicPresets: selection,
                            });
                        } else {
                            props.setFilterValue(undefined);
                        }
                    }}
                    allowCustomShape
                    customShape={
                        props.filterValue?.geometry as FeatureCollection
                    }
                    setCustomShape={(shape) => {
                        if (shape) {
                            props.setFilterValue({
                                type: "location",
                                geometry: shape,
                            });
                        } else {
                            props.setFilterValue(undefined);
                        }
                    }}
                    pendingShape={pendingShape}
                    setPendingShape={(shape) => {
                        setPendingShape(shape);
                    }}
                />
            </div>
        </BaseHeaderFilter>
    );
};

export const AssignedToFilter = (props: MultipleChoiceFilterProps) => {
    const apiClient = useAccountsApiClient();
    const dataProvidersQuery = useQuery({
        queryKey: ["userOptionsFilter"],
        queryFn: async () => {
            return await apiClient.accountsUsersList();
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const providerOptions = useMemo(() => {
        if (!dataProvidersQuery.data) {
            return [];
        }
        return dataProvidersQuery.data.map((p) => {
            return {
                id: `${p.id}`,
                displayValue: `${p.firstName} ${p.lastName}`,
            };
        });
    }, [dataProvidersQuery.data]);

    return (
        <MultipleChoiceFilterBase
            props={props}
            options={providerOptions}
            searchable
        />
    );
};

const EVENT_GLOBAL_STATUS_LIST = Object.values(
    EmissionRecordsListEventStatusEnum,
).map((item) => {
    return {
        id: item,
        displayValue: item.replaceAll("_", " ").toLowerCase(),
    };
});

export const EventGlobalStatusFilter = (props: MultipleChoiceFilterProps) => {
    return (
        <MultipleChoiceFilterBase
            props={props}
            options={EVENT_GLOBAL_STATUS_LIST}
        />
    );
};

export const ProviderFilter = (props: MultipleChoiceFilterProps) => {
    const apiClient = useDataProvidersApi();
    const dataProvidersQuery = useQuery({
        queryKey: ["dataProvidersWithData"],
        queryFn: async () => {
            return await apiClient.dataProvidersList({ hasData: true });
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const providerOptions = useMemo(() => {
        if (!dataProvidersQuery.data) {
            return [];
        }
        return dataProvidersQuery.data.results.map((p) => {
            return {
                id: p.id,
                displayValue: p.name,
            };
        });
    }, [dataProvidersQuery.data]);

    return <MultipleChoiceFilterBase props={props} options={providerOptions} />;
};

const EVENT_STATUS_LIST = Object.values(EventStatusEnum).map((item) => {
    return {
        id: item,
        displayValue: item.replaceAll("_", " ").toLowerCase(),
    };
});

export const EventStatusFilter = (props: MultipleChoiceFilterProps) => {
    return (
        <MultipleChoiceFilterBase props={props} options={EVENT_STATUS_LIST} />
    );
};

export const DecisionFilter = (props: MultipleChoiceFilterProps) => {
    return (
        <MultipleChoiceFilterBase
            props={props}
            options={DECISION_FILTER_OPTIONS}
        />
    );
};

interface DateRangeFilterProps {
    isFiltered: boolean;
    filterValue?: TableDateFilterState;
    setFilterValue: (newFilterValue?: TableDateFilterState) => void;
    extraFilterProps?: { [key: string]: any };
}

export const DateRangeFilter = (props: DateRangeFilterProps) => {
    const monthsBetweenDates = useMemo(() => {
        if (!props.filterValue?.after || !props.filterValue?.before) {
            return 0;
        }
        const d1 = props.filterValue.after.getTime();
        const d2 = props.filterValue.before.getTime();
        if (d1 > d2) {
            return 0;
        }
        const difference = Math.abs(d2 - d1);
        const months = difference / (1000 * 60 * 60 * 24 * 30);
        return Math.round(months);
    }, [props.filterValue?.after, props.filterValue?.before]);

    const updateRange = (months: number) => {
        const today = new Date();
        props.setFilterValue({
            type: "date",
            after: new Date(
                new Date().setMonth(new Date().getMonth() - months),
            ),
            before: today,
        });
    };

    const moveNPeriods = (periodsToMove: number) => {
        if (!props.filterValue?.after || !props.filterValue?.before) {
            return;
        }
        const d1 = props.filterValue.after.getTime();
        const d2 = props.filterValue.before.getTime();
        const difference = Math.abs(d2 - d1);
        props.setFilterValue({
            type: "date",
            after: new Date(d1 + periodsToMove * difference),
            before: new Date(d2 + periodsToMove * difference),
        });
    };

    return (
        <BaseHeaderFilter isFiltered={props.isFiltered}>
            <div className="bg-white p-1 rounded text-sm w-[400px]">
                <div className="flex w-full items-center justify-between mb-1">
                    <span className="font-medium tracking-wide uppercase text-xs text-zinc-600">
                        Filter by date range
                    </span>
                    <div className="flex items-center gap-2">
                        {(
                            [
                                ...(props.extraFilterProps?.disableAllOption
                                    ? []
                                    : [["All", null]]),
                                ["3m", 3],
                                ["6m", 6],
                                ["1y", 12],
                                ["3y", 12 * 3],
                                ["5y", 12 * 5],
                            ] as [string, number | null][]
                        ).map((r) => (
                            <button
                                key={r[0]}
                                type="button"
                                onClick={() =>
                                    r[1] === null
                                        ? props.setFilterValue(undefined)
                                        : updateRange(r[1])
                                }
                                className={`
                                    flex items-center gap-1 text-ae-blue-550 text-xs
                                    hover:bg-ae-blue-50 p-1 rounded
                                    ${r[1] === null && !props.filterValue ? "bg-ae-blue-50" : ""}
                                    ${r[1] !== null && monthsBetweenDates === r[1] ? "bg-ae-blue-50" : ""}
                                `}
                            >
                                {r[0]}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => moveNPeriods(-1)}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faChevronsLeft} />
                        </button>
                        <button
                            type="button"
                            onClick={() => moveNPeriods(1)}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faChevronsRight} />
                        </button>
                    </div>
                </div>
                <DateRangeField
                    valueAfter={props.filterValue?.after?.toISOString()}
                    valueBefore={props.filterValue?.before?.toISOString()}
                    onChangeAfter={(v) =>
                        props.setFilterValue({
                            type: "date",
                            after: v ? new Date(v) : undefined,
                            before: props.filterValue?.before,
                        })
                    }
                    onChangeBefore={(v) =>
                        props.setFilterValue({
                            type: "date",
                            after: props.filterValue?.after,
                            before: v ? new Date(v) : undefined,
                        })
                    }
                    showPresets={false}
                    showRangeNavigation={false}
                />
            </div>
        </BaseHeaderFilter>
    );
};

interface RangeFilterProps {
    isFiltered: boolean;
    filterValue?: RangeFilterState;
    setFilterValue: (newFilterValue?: RangeFilterState) => void;
}

export const RangeFilter = (props: RangeFilterProps) => (
    <BaseHeaderFilter
        isFiltered={
            props.isFiltered &&
            (!!props.filterValue?.min || !!props.filterValue?.max)
        }
    >
        <div className="p-1 font-normal text-sm text-ae-blue-900">
            <div className="p-1 text-sm flex gap-2 w-60">
                <div className="w-1/2">
                    Minimum:
                    <NumberField
                        value={props.filterValue?.min}
                        onChange={(v: number) => {
                            props.setFilterValue({
                                type: "range",
                                min: v,
                                max: props.filterValue?.max,
                            });
                        }}
                        min={0}
                    />
                </div>
                <div className="w-1/2">
                    Maximum:
                    <NumberField
                        value={props.filterValue?.max}
                        onChange={(v: number) => {
                            props.setFilterValue({
                                type: "range",
                                min: props.filterValue?.min,
                                max: v,
                            });
                        }}
                        min={0}
                    />
                </div>
            </div>
            {props.filterValue?.min > props.filterValue?.max && (
                <div className="text-red-500 text-sm w-60 px-1">
                    Minimum value must be smaller than maximum value.
                </div>
            )}
        </div>
    </BaseHeaderFilter>
);

interface MultipleChoiceNestedFilterProps {
    isFiltered: boolean;
    filterValue?: {
        type: "provider";
        value: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[];
    };
    setFilterValue: (newFilterValue?: {
        type: "provider";
        value: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[];
    }) => void;
    extraFilterProps?: { [key: string]: any };
}
export const DataProviderFilterV2 = ({
    isFiltered,
    filterValue,
    setFilterValue,
    extraFilterProps,
}: MultipleChoiceNestedFilterProps) => (
    <BaseHeaderFilter isFiltered={isFiltered}>
        <div className="w-[500px]">
            <DataProviderNestedFilter
                filterValue={filterValue?.value}
                setFilterValue={(newValue) =>
                    setFilterValue(
                        newValue
                            ? { type: "provider", value: newValue }
                            : undefined,
                    )
                }
                filters={extraFilterProps}
            />
        </div>
    </BaseHeaderFilter>
);

const EXTERNAL_PROVIDER_OPTIONS = [
    { id: "longpath", displayValue: "Longpath" },
    { id: "sensirion", displayValue: "Sensirion" },
];

export const ExternalProviderFilter = (props: MultipleChoiceFilterProps) => {
    return (
        <MultipleChoiceFilterBase
            props={props}
            options={EXTERNAL_PROVIDER_OPTIONS}
        />
    );
};

interface SiteEmissionThresholdFilterProps {
    isFiltered: boolean;
    filterValue?: {
        type: "emissionStats";
        value: InfrastructureSitesListEmissionStatsParameter;
    };
    setFilterValue: (newFilterValue?: {
        type: "emissionStats";
        value: InfrastructureSitesListEmissionStatsParameter;
    }) => void;
}

export const SiteEmissionThresholdFilter = (
    props: SiteEmissionThresholdFilterProps,
) => {
    const current = props.filterValue?.value;
    const [sortField, setSortField] = useAtom(siteEmissionSortFieldAtom);
    const update = (
        patch: Partial<InfrastructureSitesListEmissionStatsParameter>,
    ) => {
        props.setFilterValue({
            type: "emissionStats",
            value: { ...current, ...patch },
        });
    };

    // isFiltered need to take into account if we're actually filtering things
    const isFiltered =
        props.isFiltered && typeof current?.filterSites === "boolean";

    return (
        <BaseHeaderFilter isFiltered={isFiltered}>
            <div className="p-1 text-sm">
                <MenuCategory>Has emissions?</MenuCategory>
                <div className="flex items-center gap-2 mt-1">
                    <label className="flex items-center gap-1">
                        <input
                            type="radio"
                            checked={current?.filterSites === true}
                            onClick={() => update({ filterSites: true })}
                        />
                        Yes
                    </label>
                    <label className="flex items-center gap-1">
                        <input
                            type="radio"
                            checked={current?.filterSites === false}
                            onClick={() => update({ filterSites: false })}
                        />
                        No
                    </label>
                    <label className="flex items-center gap-1">
                        <input
                            type="radio"
                            checked={current?.filterSites === undefined}
                            onClick={() => update({ filterSites: undefined })}
                        />
                        Any
                    </label>
                </div>
                <hr className="my-2" />
                <MenuCategory>Emission filters:</MenuCategory>
                <label className="flex items-center gap-1 mt-1">
                    <input
                        type="radio"
                        checked={current?.emissionThreshold === undefined}
                        onClick={() => update({ emissionThreshold: undefined })}
                    />
                    All
                </label>
                <label className="flex items-center gap-1">
                    <input
                        type="radio"
                        checked={!!current?.emissionThreshold}
                        onClick={() =>
                            update({ emissionThreshold: 100 * 1000 })
                        }
                    />
                    Only above
                    <input
                        type="number"
                        min="1"
                        step="1"
                        className="p-0 px-1 w-20 rounded"
                        value={
                            current?.emissionThreshold !== undefined
                                ? current.emissionThreshold / 1000
                                : ""
                        }
                        onChange={(e) =>
                            update({
                                emissionThreshold:
                                    Math.max(
                                        1,
                                        Math.floor(Number(e.target.value)),
                                    ) * 1000,
                            })
                        }
                    />
                    kg/h
                </label>
                <hr className="my-2" />
                <MenuCategory>Sort by</MenuCategory>
                <div className="flex flex-col gap-1 mt-1">
                    <label className="flex items-center gap-1">
                        <input
                            type="radio"
                            checked={sortField === "emission_count"}
                            onClick={() => setSortField("emission_count")}
                        />
                        Emission count
                    </label>
                    <label className="flex items-center gap-1">
                        <input
                            type="radio"
                            checked={sortField === "emission_sum"}
                            onClick={() => setSortField("emission_sum")}
                        />
                        Total emissions
                    </label>
                </div>
            </div>
        </BaseHeaderFilter>
    );
};

export const SiteTypeFilter = (props: MultipleChoiceFilterProps) => {
    const analyticsApi = useAnalyticsApiClient();
    const siteTypesQuery = useQuery({
        queryKey: ["analyticsSiteTypes"],
        queryFn: async () => {
            return await analyticsApi.analyticsSitesSiteTypesRetrieve();
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const providerOptions = useMemo(() => {
        if (!siteTypesQuery.data) {
            return [];
        }
        return siteTypesQuery.data.map((p) => {
            return {
                id: p,
                displayValue: p.replaceAll("_", " "),
            };
        });
    }, [siteTypesQuery.data]);

    return <MultipleChoiceFilterBase props={props} options={providerOptions} />;
};
